import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/use-auth';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RETRYABLE_STATUS = [502, 503, 504];

/**
 * Thin student-facing API wrapper around authedFetch. Pass the FULL path
 * including the `/api` prefix (e.g. '/api/network/directory'). Throws a
 * friendly Error on non-2xx and parses JSON. GET requests silently retry
 * once on network failures / gateway errors so transient blips never show
 * a broken page.
 */
export const useApi = () => {
  const { authedFetch } = useAuth();

  const request = useCallback(async (path, { method = 'GET', body } = {}) => {
    const attempt = () => authedFetch(path, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    let res;
    try {
      res = await attempt();
    } catch (netErr) {
      if (method !== 'GET') throw new Error('Network error — check your connection and try again');
      await sleep(700);
      try { res = await attempt(); }
      catch { throw new Error('Network error — check your connection and try again'); }
    }
    if (!res.ok && method === 'GET' && RETRYABLE_STATUS.includes(res.status)) {
      await sleep(700);
      try { res = await attempt(); } catch { /* keep original response */ }
    }
    if (!res.ok) {
      let detail = `Request failed (${res.status})`;
      try {
        const j = await res.json();
        if (typeof j?.detail === 'string') detail = j.detail;
        else if (Array.isArray(j?.detail)) detail = j.detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
        else if (j?.detail?.message) detail = j.detail.message;
        else if (j?.detail) detail = JSON.stringify(j.detail);
      } catch { /* noop */ }
      throw new Error(detail);
    }
    if (res.status === 204) return null;
    return res.json();
  }, [authedFetch]);

  // Memoize the returned object so consumers that use `api` in a useEffect
  // dep array don't re-fire their effects on every parent re-render. Without
  // this, pages like MentorHome refetched (and briefly cleared) their data
  // on every render — the source of the "flicker" reported by mentors.
  return useMemo(() => ({
    get: (p) => request(p),
    post: (p, body) => request(p, { method: 'POST', body }),
    put: (p, body) => request(p, { method: 'PUT', body }),
    patch: (p, body) => request(p, { method: 'PATCH', body }),
    del: (p) => request(p, { method: 'DELETE' }),
    // Multipart form POST (for file uploads that skip JSON encoding).
    postForm: async (p, formData) => {
      const res = await authedFetch(p, { method: 'POST', body: formData });
      if (!res.ok) {
        let detail = `Upload failed (${res.status})`;
        try {
          const j = await res.json();
          if (typeof j?.detail === 'string') detail = j.detail;
          else if (Array.isArray(j?.detail)) detail = j.detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
          else if (j?.detail) detail = JSON.stringify(j.detail);
        } catch { /* noop */ }
        throw new Error(detail);
      }
      return res.json();
    },
    signedUrl: async (key, { preview = false } = {}) => (await request(
      `/api/uploads/signed-get?key=${encodeURIComponent(key)}${preview ? '&preview=1' : ''}`,
    )).url,
    publicUrl: async (key, { preview = false } = {}) => (await request(
      `/api/uploads/public-url?key=${encodeURIComponent(key)}${preview ? '&preview=1' : ''}`,
    )).url,
  }), [request, authedFetch]);
};

export default useApi;
